import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, LocateFixed, Loader2, ArrowRight, X } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import DealerCard from "@/components/site/DealerCard";
import {
    clearStoredLocation,
    loadStoredLocation,
    requestUserLocation,
} from "@/lib/geo";
import {
    VISIT_PURPOSES,
    hasVisitorProfile,
    hasOutletLeadCaptured,
    loadVisitor,
    markOutletLeadCaptured,
    saveVisitor,
} from "@/lib/visitor";
import { selectOption } from "@/lib/selectOption";

const NearestOutletWidget = ({
    title = "Nearest Servall outlet",
    subtitle = "Share your location once — we'll show the closest showroom for a test ride or on-road quote.",
    limit = 3,
    brand = "",
    compact = false,
    embedded = false,
    thanks = "Thanks, {name}. Your details are saved.",
    className = "",
    children = null,
}) => {
    const [profileReady, setProfileReady] = useState(() => hasVisitorProfile());
    const [thanksName, setThanksName] = useState(() => loadVisitor()?.name?.split(" ")[0] || "");
    const [profile, setProfile] = useState({ name: "", phone: "", purpose: "buy" });
    const [coords, setCoords] = useState(() => loadStoredLocation());
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [locating, setLocating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const v = loadVisitor();
        if (v) {
            setProfile({ name: v.name || "", phone: v.phone || "", purpose: v.purpose || "buy" });
            setThanksName(v.name?.split(" ")[0] || "");
        }
        setProfileReady(hasVisitorProfile());
    }, []);

    const fetchNearest = useCallback(async (location) => {
        if (!location) return;
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({
                lat: String(location.lat),
                lng: String(location.lng),
                limit: String(limit),
            });
            if (brand) params.set("brand", brand);
            const { data } = await api.get(`/dealers/nearest?${params.toString()}`);
            setDealers(data.items || []);
        } catch {
            setError("Could not load nearby outlets. Please try again.");
            setDealers([]);
        } finally {
            setLoading(false);
        }
    }, [brand, limit]);

    useEffect(() => {
        if (coords) fetchNearest(coords);
    }, [coords, fetchNearest]);

    const saveProfile = async () => {
        if (!profile.name.trim() || !profile.phone.trim()) {
            setError("Name and phone are required.");
            return;
        }
        if (profile.phone.replace(/\D/g, "").length < 10) {
            setError("Enter a valid 10-digit mobile number.");
            return;
        }
        if (!profile.purpose) {
            setError("Please select why you're visiting.");
            return;
        }
        const saved = saveVisitor(profile);
        setSubmitting(true);
        setError("");
        try {
            if (!hasOutletLeadCaptured()) {
                const stored = loadStoredLocation();
                const { data } = await api.post("/leads", {
                    intent: "find_outlet",
                    name: saved.name,
                    phone: saved.phone,
                    purpose: saved.purpose,
                    city: saved.city || "",
                    note: "Homepage — nearest outlet section (Continue)",
                    lat: stored?.lat,
                    lng: stored?.lng,
                });
                markOutletLeadCaptured(data?.id || "");
            }
            setProfileReady(true);
            setThanksName(saved.name.split(" ")[0] || "");
        } catch {
            setError("Could not save your details. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const useMyLocation = async () => {
        if (!profileReady) {
            setError("Add your name, phone & purpose first.");
            return;
        }
        setLocating(true);
        setError("");
        try {
            const location = await requestUserLocation();
            setCoords(location);
            saveVisitor({ lat: location.lat, lng: location.lng });
        } catch (e) {
            setError(e.message || "Could not get your location.");
        } finally {
            setLocating(false);
        }
    };

    const clearLocation = () => {
        clearStoredLocation();
        setCoords(null);
        setDealers([]);
        setError("");
    };

    const body = (
        <>
                {!embedded && (
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
                    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <p className="text-[11px] font-semibold text-[#E31837] tracking-wide uppercase">Locate outlet</p>
                        <h2 className={`font-display text-[#0A2540] mt-2 leading-tight ${compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"}`}>
                            {title}
                        </h2>
                        <p className="text-sm text-zinc-500 mt-2 max-w-xl">{subtitle}</p>
                    </motion.div>
                </div>
                )}

                {!profileReady && (
                    <div className={embedded ? "rounded-2xl border border-[#e8eaee] bg-white p-3 sm:p-4" : "bike-card p-4 sm:p-5 mb-5 bg-white"} data-testid="nearest-visitor-form">
                        <p className="text-sm font-semibold text-[#0A2540] mb-3">Before we find your outlet — quick details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                            <input
                                className="input py-2"
                                placeholder="Your name *"
                                value={profile.name}
                                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                data-testid="nearest-visitor-name"
                            />
                            <input
                                className="input py-2"
                                placeholder="Mobile *"
                                inputMode="tel"
                                value={profile.phone}
                                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                data-testid="nearest-visitor-phone"
                            />
                            <select
                                className="input py-2"
                                value={profile.purpose}
                                onChange={(e) => setProfile({ ...profile, purpose: e.target.value })}
                                data-testid="nearest-visitor-purpose"
                            >
                                {VISIT_PURPOSES.map((p) => selectOption(p.value, p.label))}
                            </select>
                        </div>
                        <button type="button" onClick={saveProfile} disabled={submitting} className={`btn btn-primary justify-center min-h-[44px] ${embedded ? "w-full sm:w-auto" : "btn-sm"}`} data-testid="nearest-visitor-save">
                            {submitting ? "Saving…" : "Continue"}
                        </button>
                        {embedded && error && (
                            <p className="text-sm text-[#E31837] bg-red-50 border border-red-100 rounded-xl px-4 py-3 mt-3" role="alert">{error}</p>
                        )}
                    </div>
                )}

                {profileReady && embedded && (
                    <>
                        <p className="text-sm text-[#0A2540] mb-3" data-testid="dealers-thanks">
                            {(thanks || "Thanks, {name}. Your details are saved.").split("{name}").join(thanksName)}
                        </p>
                        <div className="rounded-2xl border border-[#e8eaee] bg-white p-3 sm:p-4">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                                {!coords ? (
                                    <button
                                        type="button"
                                        onClick={useMyLocation}
                                        disabled={locating}
                                        className="inline-flex items-center justify-center gap-1.5 shrink-0 rounded-lg bg-[#E31837] hover:bg-[#c01430] text-white text-[13px] font-semibold px-3 h-11 w-full lg:h-9 lg:w-auto disabled:opacity-60"
                                        data-testid="nearest-use-location"
                                    >
                                        {locating ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                                        {locating ? "Detecting…" : "Use my location"}
                                    </button>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                                        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0A2540] h-9 whitespace-nowrap">
                                            <MapPin size={14} className="text-[#E31837]" /> Nearest
                                        </span>
                                        <button type="button" onClick={clearLocation} className="shrink-0 whitespace-nowrap text-[11px] text-[#E31837] font-semibold inline-flex items-center gap-1 h-7 px-2" data-testid="nearest-clear-location">
                                            <X size={12} /> Clear
                                        </button>
                                        <button type="button" onClick={useMyLocation} disabled={locating} className="inline-flex items-center justify-center gap-1.5 shrink-0 rounded-lg border border-[#e8eaee] bg-white text-[13px] font-semibold text-[#0A2540] px-3 h-9 hover:border-[#E31837] disabled:opacity-60">
                                            {locating ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                                            Refresh
                                        </button>
                                    </div>
                                )}
                                {children ? <div className="flex-1 min-w-0">{children}</div> : null}
                            </div>
                            {error && (
                                <p className="text-sm text-[#E31837] bg-red-50 border border-red-100 rounded-xl px-4 py-3 mt-3" role="alert">{error}</p>
                            )}
                        </div>
                    </>
                )}

                {embedded && !profileReady && children && (
                    <div className="mt-4 rounded-2xl border border-[#e8eaee] bg-white p-3 sm:p-4">
                        {children}
                    </div>
                )}

                {profileReady && !embedded && (
                    <div className="flex flex-wrap gap-2 mb-5">
                        <span className="text-xs text-zinc-500 bg-white border border-zinc-200 px-3 py-2 rounded-full">
                            Hi {loadVisitor()?.name?.split(" ")[0]} — we'll use your details if you enquire
                        </span>
                        {!coords ? (
                            <button
                                type="button"
                                onClick={useMyLocation}
                                disabled={locating}
                                className="inline-flex items-center justify-center gap-2 bg-[#0A2540] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#E31837] transition min-h-[44px] disabled:opacity-60"
                                data-testid="nearest-use-location"
                            >
                                {locating ? <Loader2 size={16} className="animate-spin" /> : <LocateFixed size={16} />}
                                {locating ? "Detecting…" : "Use my location"}
                            </button>
                        ) : (
                            <>
                                <span className="inline-flex items-center gap-2 text-sm text-zinc-600 bg-white border border-zinc-200 px-4 py-2.5 rounded-full">
                                    <MapPin size={14} className="text-[#E31837]" />
                                    Location saved
                                </span>
                                <button type="button" onClick={clearLocation} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#E31837] px-3 py-2.5" data-testid="nearest-clear-location">
                                    <X size={14} /> Clear
                                </button>
                                <button type="button" onClick={useMyLocation} disabled={locating} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2540] border border-zinc-200 bg-white px-4 py-2.5 rounded-full hover:border-[#E31837] min-h-[44px]">
                                    {locating ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                                    Refresh
                                </button>
                            </>
                        )}
                        {!embedded && (
                        <Link to="/dealers" className="inline-flex items-center gap-1 text-sm font-semibold text-[#E31837] px-4 py-2.5 hover:underline" data-testid="nearest-all-outlets">
                            All outlets <ArrowRight size={14} />
                        </Link>
                        )}
                    </div>
                )}

                {error && !embedded && (
                    <p className="text-sm text-[#E31837] bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4" role="alert">
                        {error}
                    </p>
                )}

                {profileReady && !coords && !error && !embedded && (
                    <div className="bike-card p-6 sm:p-8 text-center text-zinc-500" data-testid="nearest-prompt">
                        <LocateFixed size={28} className="mx-auto mb-3 text-zinc-300" />
                        <p className="text-sm">Tap <strong>Use my location</strong> to find the nearest Servall showroom.</p>
                        <p className="text-xs mt-2 text-zinc-400">Location stays in your browser — not stored on our servers until you submit an enquiry.</p>
                    </div>
                )}

                {coords && loading && (
                    <div className={`flex items-center justify-center gap-2 text-zinc-500 ${embedded ? "py-4" : "py-12"}`}>
                        <Loader2 size={20} className="animate-spin" />
                        Finding nearby outlets…
                    </div>
                )}

                {coords && !loading && dealers.length > 0 && (
                    <div className={`grid ${embedded ? "gap-3 md:gap-4 mt-4" : "gap-4"} ${compact ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
                        {dealers.map((d, i) => (
                            <motion.div
                                key={d.id}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                            >
                                <DealerCard dealer={d} distanceKm={d.distance_km} compact={compact} />
                            </motion.div>
                        ))}
                    </div>
                )}

                {coords && !loading && dealers.length === 0 && !error && (
                    <div className={`text-zinc-500 ${embedded ? "text-sm mt-4" : "bike-card p-8 text-center"}`}>
                        No outlets found nearby{brand ? ` for ${brand}` : ""}.
                        {!embedded && (
                            <> <Link to="/dealers" className="text-[#E31837] font-semibold hover:underline">Browse all outlets</Link></>
                        )}
                    </div>
                )}
        </>
    );

    if (embedded) {
        return (
            <div className={className} data-testid="nearest-outlet-widget">
                {body}
            </div>
        );
    }

    return (
        <section
            className={`${compact ? "py-8" : "py-10 sm:py-14"} bg-[#F4F6F8] border-t border-zinc-100 ${className}`}
            data-testid="nearest-outlet-widget"
        >
            <div className="container-x">
                {body}
            </div>
        </section>
    );
};

export default NearestOutletWidget;
